/**
 * Out-Play / ArenaX — central analytics helper (Phase 178.6).
 *
 * One canonical surface for all UI interaction telemetry.
 *
 * Why centralise?
 *   - Every menu primitive (`<BulletproofMenu>`, `<BulletproofContextMenu>`)
 *     and every operator surface (Booking Inbox, Slot Calendar, Cockpits)
 *     emit interaction events.  Without a central pipe we'd end up with
 *     8 different patterns and no way to bulk-route them to PostHog /
 *     Mixpanel / internal warehouse.
 *   - Decouples emit-site from sink: today we buffer to `window` + console
 *     in dev; tomorrow we can swap in PostHog without touching any caller.
 *
 * Behavior:
 *   - Lightweight, async-safe.  Never blocks the calling thread.
 *   - Swallows all errors — analytics MUST NOT crash a UI interaction.
 *   - Buffers events on `window.__outplay_events__` (capped at 500 — drops
 *     oldest) so dev/QA can inspect via `window.__outplay_events__` in DevTools.
 *   - Calls `window.__OUTPLAY_TRACK__(event, payload)` if defined — that
 *     hook is what PostHog/Mixpanel will plug into during Phase 178.6.E.
 *
 * Usage:
 *   import { track } from '@/lib/analytics';
 *   track('menu_navigate', { source: 'arenax_avatar', destination: '/dashboard' });
 */

const MAX_BUFFER = 500;

export function track(event, payload = {}) {
  if (typeof window === 'undefined') return;

  // 1. Buffer locally — capped, FIFO drop.
  try {
    if (!window.__outplay_events__) window.__outplay_events__ = [];
    const buf = window.__outplay_events__;
    buf.push({
      event,
      payload,
      at: new Date().toISOString(),
    });
    if (buf.length > MAX_BUFFER) buf.shift();
  } catch (_) { /* non-fatal */ }

  // 2. Forward to the production analytics hook if installed.
  //    Async via queueMicrotask so the emit-site never blocks waiting on
  //    PostHog's network hop.
  try {
    if (typeof window.__OUTPLAY_TRACK__ === 'function') {
      queueMicrotask(() => {
        try { window.__OUTPLAY_TRACK__(event, payload); } catch (_) { /* swallow */ }
      });
    }
  } catch (_) { /* non-fatal */ }

  // 3. Dev console mirror — opt-in via localStorage flag so we don't
  //    spam production console.
  try {
    if (typeof localStorage !== 'undefined'
        && localStorage.getItem('outplay_log_analytics') === '1') {
      // eslint-disable-next-line no-console
      console.debug(`📊 ${event}`, payload);
    }
  } catch (_) { /* non-fatal */ }
}

/**
 * Convenience helper for menu primitives.  Maps the
 * `{source, destination, capability_context}` shape used by
 * `<BulletproofMenu>` into the canonical event name.
 */
export function trackMenuNavigate(payload) {
  track('menu_navigate', payload);
}

/**
 * Convenience helper for context menu primitives.
 *   verb: 'opened' | 'selected' | 'cancelled'
 */
export function trackContextMenu(verb, payload) {
  const event = verb === 'opened' ? 'context_menu_opened'
              : verb === 'selected' ? 'context_action_selected'
              : verb === 'cancelled' ? 'context_menu_cancelled'
              : `context_menu_${verb}`;
  track(event, payload);
}
